import { useCallback } from 'react';
import { ConditionsNew } from '@retail/calculus/types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { CreateOrUpdateMarkupForm, createOrUpdateMarkupSchema, CreateOrUpdateMarkupSchema } from '@retail/markup/components';
import { calculateCoverageFromSalesFactor } from '@retail/utils';
import { useConditionActions } from '@retail/calculus/context';
import { EditConditionDialog } from '../ConditionActionsBase';

interface Props {
  markupToEdit: ConditionsNew;
  isOpen: boolean;
  onClose: () => void;
}

export function EditMarkupDialog({ markupToEdit, isOpen, onClose }: Props) {
  const t = useCalculusTFunction();

  const formMethods = useForm<CreateOrUpdateMarkupSchema>({
    defaultValues: {
      salesCoverage: markupToEdit.markupCondition?.coverageDegree
        ? markupToEdit.markupCondition?.coverageDegree + ''
        : calculateCoverageFromSalesFactor(markupToEdit.markupCondition!.salesFactor!).toFixed(2) +
        '',
      salesFactor: markupToEdit.markupCondition?.salesFactor + '',
      fromDate: new Date(),
      toDate: markupToEdit.markupCondition?.validTo
        ? new Date(markupToEdit?.markupCondition?.validTo)
        : null,
    },
    resolver: yupResolver(createOrUpdateMarkupSchema(t)),
  });

  const { createOrUpdateCondition } = useConditionActions();

  const onActivate = useCallback(
    ({ fromDate, salesFactor }: CreateOrUpdateMarkupSchema) => {
      createOrUpdateCondition({
        content: { markup: { salesFactor: Number(salesFactor) } },
        bindingValue: markupToEdit.getBindingLevel(),
        validFrom: fromDate,
      }).then(onClose);
    },
    [createOrUpdateCondition, markupToEdit, onClose]
  );

  return (
    <EditConditionDialog
      open={isOpen}
      title={t('calculus.actions.editEntry', { name: markupToEdit.bindingTargetName })}
      dialogContent={
        <FormProvider {...formMethods}>
          <CreateOrUpdateMarkupForm />
        </FormProvider>
      }
      isDisabled={Object.keys(formMethods?.formState?.errors).length > 0}
      onSubmit={formMethods.handleSubmit(onActivate)}
      onClose={onClose}
    />
  );
}
